import { useRef } from "react";
import { sanitizeArticleHtml } from "../lib/safeHtml";

interface RichTextEditorProps {
  initialValue: string;
  onChange: (html: string) => void;
}

const TOOLBAR: { label: string; command: string; arg?: string; title: string }[] = [
  { label: "B", command: "bold", title: "Bold" },
  { label: "I", command: "italic", title: "Italic" },
  { label: "U", command: "underline", title: "Underline" },
  { label: "H2", command: "formatBlock", arg: "H2", title: "Heading" },
  { label: "¶", command: "formatBlock", arg: "P", title: "Paragraph" },
  { label: "• List", command: "insertUnorderedList", title: "Bullet list" },
  { label: "1. List", command: "insertOrderedList", title: "Numbered list" },
  { label: "Clear", command: "removeFormat", title: "Clear formatting" },
];

/** Minimal hand-rolled WYSIWYG for article bodies -- a contentEditable div
 *  with a document.execCommand toolbar, no external editor dependency.
 *  execCommand is deprecated but still broadly supported for this basic set
 *  of commands, and pulling in a full editor library (TipTap/Quill/etc.) felt
 *  like overkill for "bold/italic/lists/links" on a club news feed.
 *
 *  Uncontrolled by design: initialValue seeds the DOM once on mount via
 *  dangerouslySetInnerHTML and is never pushed back in on re-render (that
 *  would reset the cursor position on every keystroke). The caller forces a
 *  reset -- e.g. switching from "create" to "edit an existing article" --
 *  by remounting with a different `key` prop, the same pattern a plain
 *  <input defaultValue> relies on. */
export function RichTextEditor({ initialValue, onChange }: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null);

  function exec(command: string, arg?: string) {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  }

  function handleLink() {
    const url = prompt("Link URL:");
    if (url) exec("createLink", url);
  }

  return (
    <div className="rich-text-editor">
      <div className="rich-text-toolbar">
        {TOOLBAR.map((t) => (
          <button key={t.label} type="button" title={t.title} onClick={() => exec(t.command, t.arg)}>
            {t.label}
          </button>
        ))}
        <button type="button" title="Insert link" onClick={handleLink}>Link</button>
      </div>
      <div
        ref={ref}
        className="rich-text-body"
        contentEditable
        suppressContentEditableWarning
        dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(initialValue) }}
        onInput={() => ref.current && onChange(ref.current.innerHTML)}
      />
    </div>
  );
}
